import { useEffect, useMemo, useRef, useState } from 'react';
import { guideAnsichtSteuerung, useGuideAnsichtSteuerung } from '../lib/guideAnsichtSteuerung';
import { setHeuteWahl, useHeuteWahl } from '../lib/useUiPreferences';
import { bereinigeHeuteWahl, heuteAnordnung, istGeaendert, pinneEintrag, setzeZu, spaeterEintrag, tagesSchluessel, verschiebeEintrag, zumVorschlag } from './guide-heute';
import { STATUS_FILTER_WORT, statusVon, type OberthemaBuehne } from './guide-oberthemen';
import './GuideHeuteDran.css';

const kurz = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);

/**
 * „HEUTE DRAN“ — die Leiste ueber den Kacheln in „Ziele (neu)“ (Rafael, 25.10.2026): hoechstens fuenf Eintraege als VORSCHLAG fuer heute, je mit dem Grund in einem Wort.
 * Ein Klick oeffnet die Buehne wie bisher. „Ordnen“ zeigt je Eintrag Pfeile, „Als Nächstes“ und „Später“; die Aenderung gilt nur HEUTE (Schluessel Tag + Nutzer, ueber
 * useUiPreferences), morgen beginnt wieder der Vorschlag. Eingeklappt ist es eine Zeile. Der Vorschlag selbst kommt aus der reinen Funktion in guide-heute.ts.
 */
export function HeuteDran<T extends OberthemaBuehne>({ zeilen, nutzer, onOeffnen }: { zeilen: T[]; nutzer: string; onOeffnen: (b: T) => void }) {
  const jetzt = Date.now();
  const tag = tagesSchluessel(jetzt);
  const gespeichert = useHeuteWahl(nutzer, tag);
  const [ordnen, setOrdnen] = useState(false);
  /* Der Tastaturfokus geht nie verloren: nach einer Aenderung wird er auf das gemerkte Ziel gesetzt (React verschiebt beim Umordnen die Knoten). */
  const fokus = useRef<{ id: string | null; knopf: string } | null>(null);
  const liste = useRef<HTMLElement>(null);
  useEffect(() => {
    const f = fokus.current;
    if (!f) return;
    fokus.current = null;
    const el = f.id ? liste.current?.querySelector<HTMLElement>(`[data-ai-id="guide-heute-${CSS.escape(f.id)}"] [data-hd="${f.knopf}"]`) : liste.current?.querySelector<HTMLElement>('[data-ai-id="guide-heute-ordnen"]');
    el?.focus();
  });
  const steuer = useGuideAnsichtSteuerung();
  const bekannt = useMemo(() => new Set(zeilen.map(b => b.id)), [zeilen]);
  /* Beim Lesen verworfen: abgeschlossene oder unbekannte Buehnen aus Rafaels Wahl. */
  const wahl = useMemo(() => bereinigeHeuteWahl(gespeichert, bekannt), [gespeichert, bekannt]);
  const eintraege = heuteAnordnung(zeilen, wahl, jetzt, { vorschlagIds: steuer.reihenfolgeVorschlag?.ids });
  const ids = eintraege.map(e => e.b.id);
  const aendere = (neu: typeof wahl) => setHeuteWahl(nutzer, tag, neu);
  const geaendert = istGeaendert(wahl);
  /* Nicht in der Leiste, aber nicht verschwiegen: Buehnen, denen der Berater keinen naechsten Schritt eingetragen hat. */
  const ohneSchritt = useMemo(() => zeilen.filter(b => statusVon(b) === 'ohneSchritt').length, [zeilen]);
  const assistentVorschlag = steuer.reihenfolgeVorschlag && steuer.reihenfolgeVorschlag.ids.length > 0 ? steuer.reihenfolgeVorschlag : null;

  return (
    <>
    <section ref={liste} className="hd" data-ai-id="guide-heute-dran" aria-label="Heute dran — Vorschlag">
      <div className="hd-kopf">
        <button type="button" className="hd-zu" data-ai-id="guide-heute-einklappen" aria-expanded={!wahl.zu} aria-controls="hd-liste"
          onClick={() => aendere(setzeZu(wahl, !wahl.zu))}>
          <span aria-hidden="true">{wahl.zu ? '▸' : '▾'}</span> Heute dran
        </button>
        <span className="hd-vorschlag" title={assistentVorschlag ? `Vorschlag deines Assistenten: ${assistentVorschlag.begruendung}` : 'Ein Vorschlag aus Frist, „braucht dich“, entblockter Arbeit und Zielarbeit. Du kannst ihn für heute ändern.'}>
          {assistentVorschlag ? 'Vorschlag (Assistent)' : 'Vorschlag'}
        </span>
        <span className="hd-zahl" aria-label={`${eintraege.length} Einträge`}>{eintraege.length}</span>
        {wahl.zu && eintraege.length > 0 && <span className="hd-vorschau" title={eintraege.map(e => e.b.titel).join('\n')}>{kurz(eintraege[0].b.titel, 44)} · {eintraege[0].grund}</span>}
      </div>
      {!wahl.zu && (
        eintraege.length === 0
          ? <div className="hd-leer" data-ai-id="guide-heute-leer">Nichts Eiliges im Vorschlag{wahl.spaeter.length > 0 ? ' — der Rest ist für heute auf „Später“' : ''}.</div>
          : (
            <ol className="hd-liste" id="hd-liste" role="list">
              {eintraege.map((e, i) => (
                <li key={e.b.id} className={`hd-chip${e.pinned ? ' hd-chip--pin' : ''}`} data-ai-id={`guide-heute-${e.b.id}`} data-pinned={e.pinned ? 'ja' : undefined}>
                  <button type="button" className="hd-oeffnen" onClick={() => onOeffnen(e.b)}
                    title={`${e.b.titel}\n${e.grund}${e.aufwand ? ` · ${e.aufwand}` : ''}${e.pinned ? '\nAls Nächstes' : ''}`}>
                    <span className="hd-nr" aria-hidden="true">{e.pinned ? '📌' : i + 1}</span>
                    <span className="hd-titel">{kurz(e.b.titel, 20)}</span>
                    <span className="hd-grund">{e.grund}</span>
                    {e.aufwand && <span className="hd-aufwand">{e.aufwand.replace('ca. ', '')}</span>}
                  </button>
                  {ordnen && (
                    <span className="hd-steuer" role="group" aria-label={`${e.b.titel} ordnen`}>
                      {/* Am Rand nicht `disabled` (das nimmt den Fokus), sondern aria-disabled: der Knopf bleibt fokussierbar, der Klick tut nichts. */}
                      <button type="button" className="hd-knopf" data-hd="hoch" aria-label="Nach oben" title="Nach oben" aria-disabled={i === 0 || undefined}
                        onClick={() => { if (i === 0) return; fokus.current = { id: e.b.id, knopf: 'hoch' }; aendere(verschiebeEintrag(wahl, ids, e.b.id, -1)); }}>↑</button>
                      <button type="button" className="hd-knopf" data-hd="runter" aria-label="Nach unten" title="Nach unten" aria-disabled={i === eintraege.length - 1 || undefined}
                        onClick={() => { if (i === eintraege.length - 1) return; fokus.current = { id: e.b.id, knopf: 'runter' }; aendere(verschiebeEintrag(wahl, ids, e.b.id, 1)); }}>↓</button>
                      <button type="button" className="hd-knopf" data-hd="pin" aria-pressed={e.pinned} title="Als Nächstes"
                        onClick={() => { fokus.current = { id: e.b.id, knopf: 'pin' }; aendere(pinneEintrag(wahl, e.b.id)); }}>Als Nächstes</button>
                      <button type="button" className="hd-knopf" data-hd="spaeter" title="Für heute ausblenden"
                        onClick={() => { const nachbar = ids[i + 1] ?? ids[i - 1] ?? null; fokus.current = nachbar ? { id: nachbar, knopf: 'spaeter' } : { id: null, knopf: 'ordnen' }; aendere(spaeterEintrag(wahl, e.b.id)); }}>Später</button>
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )
      )}
      {!wahl.zu && (
          <span className="hd-aktionen">
            {geaendert && <button type="button" className="hd-knopf" data-ai-id="guide-heute-vorschlag" title="Zurück zum Vorschlag (Reihenfolge, „Als Nächstes“ und „Später“ für heute verwerfen)" onClick={() => aendere(zumVorschlag(wahl))}>Zum Vorschlag</button>}
            {eintraege.length > 0 && <button type="button" className="hd-knopf" data-ai-id="guide-heute-ordnen" aria-pressed={ordnen} title="Reihenfolge für heute ändern" onClick={() => setOrdnen(o => !o)}>Ordnen</button>}
          </span>
      )}
    </section>
    {ohneSchritt > 0 && (
      <div className="hd-hinweis" data-ai-id="guide-heute-ohne-schritt">
        <span aria-hidden="true">❓</span> {ohneSchritt} {ohneSchritt === 1 ? 'Bühne' : 'Bühnen'} ohne nächsten Schritt — der Berater hat ihn nicht eingetragen.{' '}
        <button type="button" className="hd-link" onClick={() => guideAnsichtSteuerung.befehl({ filter: 'ohneSchritt', ansicht: 'oberthema' }, STATUS_FILTER_WORT.ohneSchritt)}>anzeigen</button>
      </div>
    )}
    </>
  );
}
